import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { fetchProfiles } from 'flow/actions/profile-actions';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import MenuItemAgentProfiles from 'flow/components/pbx/supervisor/AgentRow/MenuItemAgentProfiles';
import MenuItem from 'flow/components/ui-components/MenuItem';
import useAppContext from 'flow/hooks/useAppContext';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import {
    selectExtension,
    selectOwnExtensionKey,
    selectOwnUserHasCapabilityVideo1on1,
} from 'flow/selectors/ExtensionSelectors';
import { selectIsAdmin } from 'flow/selectors/own-extension-selectors';
import { ChatUserKey, ExtensionKey } from 'flow/types/EntityKeys';
import { addSupervisorLogEntry } from 'flow/utils/browser-utils';
import CallButtonMenuItem from './CallButtonMenuItem';
import VideoCallButtonMenuItem from './VideoCallButtonMenuItem';

interface Props {
    extensionKey: ExtensionKey;
}

const SupervisorAgentContextMenu = ({ extensionKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const ownUserHasVideo1on1Capability = useAppSelector(
        selectOwnUserHasCapabilityVideo1on1,
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { hasFeature } = useFeatureFlags();
    const isAdmin = useAppSelector(selectIsAdmin);
    const extension = useAppSelector((state) =>
        selectExtension(state, extensionKey),
    );

    useEffect(() => {
        if (isAdmin || ownExtensionKey === extensionKey) {
            dispatch(fetchProfiles(extensionKey));
        }
    }, [dispatch, extensionKey, isAdmin, ownExtensionKey]);

    const navigateToChat = (chatUserKey: ChatUserKey) =>
        history.push(
            generatePath(routes['messages.id.redirect'], { chatUserKey }),
        );

    const goToChat = () => {
        navigateToChat(extension.get('chatUserKey'));
        addSupervisorLogEntry('Agent menu -> Chat clicked');
    };
    const logMakeCallClicked = () => {
        addSupervisorLogEntry('Agent menu -> Call clicked');
    };

    const disableButtonForOwnUser = ownExtensionKey === extensionKey;

    if (!extension) {
        return null;
    }

    const canMakeVideoCall =
        extension &&
        ownUserHasVideo1on1Capability &&
        hasFeature(FeatureFlags.VIDEO_CANVAS);

    return (
        <>
            <CallButtonMenuItem
                disableButton={disableButtonForOwnUser}
                fixedNumber={extension.getIn(['contact', 'fixed', 'number'])}
                technicalNumber={extension.getIn([
                    'contact',
                    'technicalNumber',
                    'number',
                ])}
                mobileNumber={extension.getIn(['contact', 'mobile', 'number'])}
                extensionToCall={extension}
                onMakeCallClicked={
                    !disableButtonForOwnUser ? () => logMakeCallClicked() : null
                }
            />

            {canMakeVideoCall && (
                <VideoCallButtonMenuItem extensionKey={extension.get('key')} />
            )}

            <MenuItem
                onClick={!disableButtonForOwnUser ? () => goToChat() : null}
                disabled={disableButtonForOwnUser}
            >
                {`${i18next.t('Tooltip.Title.SendMessage')}...`}
            </MenuItem>

            {isAdmin || extensionKey === ownExtensionKey ? (
                <MenuItemAgentProfiles extensionKey={extensionKey} />
            ) : null}
        </>
    );
};

export default React.memo(SupervisorAgentContextMenu);
